import { Question as QuestionProps } from "types/question";

import Dialog from "components/dialog";

import router from "next/router";
import { track } from "utils/tracker";

import { useMounted } from "hooks/react";

interface AnswerResultDialogProps {
  question: QuestionProps;
  questionnaireAnswer: any;
  nextQuestionPath: string;
  lessonsPath: string;
  onPaginateNext: () => void;
  onClose: () => void;
  onRevealAnswer: () => void;
  onComplete: (origin: string) => void;
}

export default function AnswerResultDialog(
  props: AnswerResultDialogProps
): JSX.Element {
  const { questionnaireAnswer, nextQuestionPath } = props;
  const { onPaginateNext, onClose } = props;

  const isCorrect =
    questionnaireAnswer.answer ===
    questionnaireAnswer.question.correctAlternative;

  function handlePagination() {
    onClose();
    router.push(nextQuestionPath);
    onPaginateNext();
  }

  if (isCorrect) {
    return <CorrectAnswerDialog {...props} onPaginate={handlePagination} />;
  } else {
    return <WrongAnswerDialog {...props} onPaginate={handlePagination} />;
  }
}

function CorrectAnswerDialog(props) {
  const { nextQuestionPath } = props;
  const { onPaginate, onComplete, onRevealAnswer, onClose } = props;

  const confirmText = nextQuestionPath
    ? "Próxima questão"
    : "Terminar simulado";

  useMounted(onRevealAnswer);

  function handleConfirm() {
    if (nextQuestionPath) return onPaginate();

    onComplete("Answer result");
  }

  return (
    <Dialog
      title="Resposta certa! 😀"
      type="success"
      confirmText={confirmText}
      cancelText="Revisar questão"
      onConfirm={handleConfirm}
      onCancel={onClose}
      className="md:ml-[270px]"
    ></Dialog>
  );
}

function WrongAnswerDialog(props) {
  const { question, lessonsPath, nextQuestionPath } = props;
  const { onRevealAnswer, onPaginate, onComplete, onClose } = props;

  function handleConfirmWithLessons() {
    track("Clicked lessons link on question", {
      origin: "Answer confirmation"
    });
    onClose();
    router.push(lessonsPath);
  }

  function handleConfirmWithoutLessons() {
    if (nextQuestionPath) return onPaginate();
    onClose();
    onComplete("Answer result");
  }

  function closeDialog() {
    onClose();
    onRevealAnswer();
  }

  if (!!question.resolution) {
    return (
      <Dialog
        title="Resposta errada 😕"
        type="error"
        confirmText="Ver explicação"
        onCancel={closeDialog}
        className="md:ml-[270px]"
      >
        <p className="dialog-text">
          Dá uma olhada na nossa explicação sobre a resposta!
        </p>
      </Dialog>
    );
  }

  if (lessonsPath) {
    return (
      <Dialog
        title="Resposta errada 😕"
        type="error"
        confirmText="Assistir aulas"
        cancelText="Ver resposta"
        onConfirm={handleConfirmWithLessons}
        onCancel={closeDialog}
        className="md:ml-[270px]"
      >
        <p className="dialog-text">
          Quer dar uma olhada nas nossas aulas sobre o assunto?
        </p>
      </Dialog>
    );
  }

  const confirmText = nextQuestionPath
    ? "Próxima questão"
    : "Terminar simulado";

  return (
    <Dialog
      title="Resposta errada 😕"
      type="error"
      confirmText={confirmText}
      cancelText="Ver resposta"
      onConfirm={handleConfirmWithoutLessons}
      onCancel={closeDialog}
      className="md:ml-[270px]"
    >
      <p className="dialog-text">Mas não tem problema! Continue estudando.</p>
    </Dialog>
  );
}
